import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { forecastAPI } from '../services/api';
import { FROM_IMAGE_API_PATH } from './achievements/shared';
import theme from '../theme';

export const FORECAST_TITLES = ['RUB/USD', 'BTC/USD', 'GOLD/USD'];
export const LANGUAGES = ['ru', 'en'];

export const FORECAST_FIELD_LABELS = {
  en: {
    direction: 'Direction',
    subtitle: 'Subtitle',
    forecastDate: 'Forecast Date',
    priceAsOfReleaseMonth: 'Price as of Release (month)',
    nextTargetMonth: 'Next Target (month)',
    priceAsOfRelease: 'Price as of Release',
    nextTarget: 'Next Target',
    currentPrice: 'Current Price',
    potential: 'Potential (%)',
    dataValidAsOf: 'Data valid as of',
    longTermTrend: 'Long-term trend',
    publishTime: 'Publish time (signal)',
    publishOnSignal: 'Publish on rate signal immediately',
    stamp: 'Stamp',
    stampDate: 'Stamp date',
    channelCaption: 'Channel caption (optional)',
  },
};

export const getForecastFieldLabels = () => FORECAST_FIELD_LABELS.en;

const RU_MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const EN_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const defaultForecastForm = {
  forecast_date: '',
  title: 'BTC/USD',
  lang: 'ru',
  basis: 0,
  subbasis: '',
  initial_price: '',
  last_price: '',
  next_targets: '',
  long_term_trend: '',
  target_date: '',
  description: 'Current forecast',
  process_now: true,
  publish_time: '01:00',
  target_roi: 29,
  end_date: '',
  stamp: '',
  stamp_date: '',
  channel_caption: '',
};

export const isArchivedStamp = (stampStatus) => {
  const normalized = String(stampStatus || '').trim().toLowerCase();
  return normalized === 'done' || normalized === 'undone';
};

export const getForecastLangKey = (forecast) => {
  if (forecast.title === 'RUB/USD') return 'ru';
  return (forecast.lang || 'ru').toLowerCase();
};

export const splitForecastsByArchiveStatus = (forecasts) => ({
  current: forecasts.filter((forecast) => !isArchivedStamp(forecast.stamp_status)),
  historical: forecasts.filter((forecast) => isArchivedStamp(forecast.stamp_status)),
});

export const canClearForecastStamp = (forecast, forecasts) => {
  if (!isArchivedStamp(forecast.stamp_status)) return true;
  const otherActive = forecasts.some(
    (item) => item.id !== forecast.id && !isArchivedStamp(item.stamp_status),
  );
  return !otherActive;
};

export const summarizeForecastsBySymbol = (forecasts) => {
  const summary = {};
  FORECAST_TITLES.forEach((title) => {
    summary[title] = title === 'RUB/USD'
      ? { current: 0, historical: 0 }
      : {
        ru: { current: 0, historical: 0 },
        en: { current: 0, historical: 0 },
      };
  });

  forecasts.forEach((forecast) => {
    if (!FORECAST_TITLES.includes(forecast.title)) return;
    const archived = isArchivedStamp(forecast.stamp_status);
    if (forecast.title === 'RUB/USD') {
      if (archived) summary[forecast.title].historical += 1;
      else summary[forecast.title].current += 1;
      return;
    }
    const lang = getForecastLangKey(forecast);
    if (lang !== 'ru' && lang !== 'en') return;
    if (archived) summary[forecast.title][lang].historical += 1;
    else summary[forecast.title][lang].current += 1;
  });

  return summary;
};

export const targetsToInput = (rawTargets) => {
  if (!rawTargets) return '';
  try {
    const parsed = JSON.parse(rawTargets);
    return Array.isArray(parsed) ? parsed.join(', ') : '';
  } catch {
    return '';
  }
};

export const inputToTargets = (value) => {
  const targets = value
    .split(',')
    .map((target) => target.trim())
    .filter(Boolean);
  return JSON.stringify(targets);
};

export const nullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const translateMonthString = (text, targetLang) => {
  if (!text) return text;
  let result = String(text);
  if (targetLang === 'en') {
    RU_MONTHS.forEach((ru, idx) => {
      result = result.replace(new RegExp(ru, 'gi'), EN_MONTHS[idx]);
    });
  } else if (targetLang === 'ru') {
    EN_MONTHS.forEach((en, idx) => {
      result = result.replace(new RegExp(en, 'gi'), RU_MONTHS[idx]);
    });
  }
  return result;
};

const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:  return "st";
    case 2:  return "nd";
    case 3:  return "rd";
    default: return "th";
  }
};

export const formatEnglishEndDate = (text) => {
  if (!text) return text;
  const match = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{2}:\d{2})$/);
  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = match[3];
    const time = match[4];
    if (month >= 1 && month <= 12) {
      return `${day}${getOrdinalSuffix(day)} ${EN_MONTHS[month - 1]} ${year} ${time}`;
    }
  }
  return text;
};

export const shiftDateString = (dateStr, hoursOffset) => {
  if (!dateStr) return dateStr;
  const match = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{2}:\d{2})$/);
  if (!match) return dateStr;
  
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const year = parseInt(match[3], 10);
  const [hour, minute] = match[4].split(':').map(Number);
  
  const date = new Date(Date.UTC(year, month, day, hour, minute));
  date.setUTCHours(date.getUTCHours() + hoursOffset);
  
  const newDay = String(date.getUTCDate()).padStart(2, '0');
  const newMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const newYear = date.getUTCFullYear();
  const newHour = String(date.getUTCHours()).padStart(2, '0');
  const newMinute = String(date.getUTCMinutes()).padStart(2, '0');
  
  return `${newDay}.${newMonth}.${newYear} ${newHour}:${newMinute}`;
};



export const buildForecastPayload = (formData) => ({
  forecast_date: formData.forecast_date,
  title: formData.title,
  lang: formData.title === 'RUB/USD' ? null : formData.lang,
  basis: nullableNumber(formData.basis),
  subbasis: formData.subbasis.trim(),
  initial_price: nullableNumber(formData.initial_price),
  last_price: nullableNumber(formData.last_price),
  next_targets: inputToTargets(formData.next_targets),
  long_term_trend: formData.long_term_trend.trim(),
  target_date: formData.target_date.trim(),
  description: formData.description.trim() || 'Current forecast',
  process_now: formData.process_now,
  publish_time: formData.publish_time || null,
  stamp_status: formData.stamp === '' ? '' : (formData.stamp || null),
  stamp_date:
    formData.stamp
      ? (formData.stamp_date?.trim() || null)
      : '',
});

export const getFirstTarget = (rawTargets) => {
  try {
    const parsed = JSON.parse(rawTargets || '[]');
    return Number(parsed?.[0]) || 0;
  } catch {
    return 0;
  }
};

export const mapForecastTitleToPair = (title) => {
  if (title === 'BTC/USD') return 'БИТКОИН/ДОЛЛАР';
  if (title === 'GOLD/USD') return 'ЗОЛОТО/ДОЛЛАР';
  return 'РУБЛЬ/ДОЛЛАР';
};

export const buildForecastDownloadName = (forecast) => {
  const symbol = (forecast.title || '').split('/')[0] || 'FORECAST';
  if (symbol === 'RUB') return 'forecast_RUB.png';
  const lang = (forecast.lang || 'ru').toUpperCase();
  return `forecast_${symbol}_${lang}.png`;
};

export const calculateTargetRoi = (forecast) => {
  const lower = nullableNumber(forecast.initial_price);
  const target = getFirstTarget(forecast.next_targets);
  if (!lower || !target) return 0;
  return Math.round((Math.abs(lower - target) / lower) * 100);
};

export const forecastToDraft = (forecast) => {
  const defaultBasis = forecast.title === 'RUB/USD' ? 1 : 0;
  return {
    title: forecast.title || 'BTC/USD',
    lang: forecast.lang || 'ru',
    basis: forecast.basis ?? defaultBasis,
    subbasis: forecast.subbasis || '',
    forecast_date: forecast.forecast_date || '',
    target_date: forecast.target_date || '',
    initial_price: forecast.initial_price ?? '',
    next_targets: targetsToInput(forecast.next_targets),
    last_price: forecast.last_price ?? '',
    long_term_trend: forecast.long_term_trend || '',
    target_roi: calculateTargetRoi(forecast) || 29,
    end_date: '',
    description: forecast.description || 'Current forecast',
    process_now: Boolean(forecast.process_now),
    publish_time: forecast.publish_time || '01:00',
    stamp: forecast.stamp_status || '',
    stamp_date: forecast.stamp_date || '',
    channel_caption: '',
  };
};

export const buildGenerationPayload = (forecast, draft = {}, options = {}) => {
  const { sendToChannel = false } = options;
  const basis = nullableNumber(draft.basis) ?? (forecast.title === 'RUB/USD' ? 1 : 0);
  const nextTarget = getFirstTarget(inputToTargets(draft.next_targets ?? targetsToInput(forecast.next_targets)));
  const targetRoi = nullableNumber(draft.target_roi) ?? calculateTargetRoi(forecast);
  const lang = draft.lang || forecast.lang || 'ru';

  return {
    telegram_id: 0,
    pair: mapForecastTitleToPair(forecast.title),
    lang,
    basis,
    subbasis: (draft.subbasis || forecast.description || '').trim(),
    forecast_date: draft.forecast_date || forecast.forecast_date || '',
    forecast_rate: nullableNumber(draft.initial_price) ?? nullableNumber(forecast.initial_price) ?? 0,
    next_target: nextTarget,
    result: nullableNumber(draft.last_price) ?? nullableNumber(forecast.last_price) ?? nullableNumber(forecast.initial_price) ?? 0,
    end_date: lang === 'en' ? formatEnglishEndDate(draft.end_date || '') : (draft.end_date || ''),
    long_term_trend: draft.long_term_trend || forecast.long_term_trend || '',
    target_date: draft.target_date || forecast.target_date || '',
    target_roi: targetRoi ?? 0,
    forecast_id: forecast.id,
    attach_to_forecast: true,
    send_to_telegram: false,
    send_to_channel: Boolean(sendToChannel),
    channel_caption: draft.channel_caption?.trim() || null,
    stamp: draft.stamp || null,
    stamp_date: draft.stamp_date || null,
  };
};

const pickerCardClass = `group flex items-center gap-4 p-4 ${theme.cardBg} rounded-2xl ${theme.cardBorder} cursor-pointer transition-all duration-200 hover:shadow-md hover:border-olive-300 hover:-translate-y-0.5 active:scale-[0.98] text-left`;

const SymbolPicker = ({ summary, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
    {FORECAST_TITLES.map((title) => {
      const stats = summary[title];
      const currentCount = title === 'RUB/USD' ? stats.current : stats.ru.current + stats.en.current;
      const historicalCount = title === 'RUB/USD' ? stats.historical : stats.ru.historical + stats.en.historical;

      return (
        <button key={title} type="button" onClick={() => onSelect(title)} className={pickerCardClass}>
          <div className="w-11 h-11 rounded-xl flex-shrink-0 bg-olive-50 flex items-center justify-center border border-olive-100">
            <span className={`text-xs font-bold ${theme.textPrimary}`}>{title.split('/')[0]}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-bold ${theme.textPrimary} group-hover:text-olive-600 transition-colors`}>{title}</h3>
            <p className={`text-xs ${theme.textMuted} mt-0.5`}>
              {currentCount} current · {historicalCount} historical
            </p>
          </div>
          <svg className="w-4 h-4 text-olive-300 group-hover:text-olive-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      );
    })}
  </div>
);

const LanguagePicker = ({ title, summary, onSelect }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5">
    {LANGUAGES.map((lang) => {
      const stats = summary[title][lang];
      return (
        <button
          key={lang}
          type="button"
          onClick={() => onSelect(lang)}
          className={`group flex flex-col items-start gap-1 px-3 py-2.5 ${theme.cardBg} rounded-xl ${theme.cardBorder} cursor-pointer transition-all duration-200 hover:shadow-md hover:border-olive-300 hover:-translate-y-0.5 active:scale-[0.97]`}
        >
          <span className={`text-sm font-bold ${theme.textPrimary} group-hover:text-olive-600 transition-colors`}>
            {lang.toUpperCase()}
          </span>
          <span className={`text-[11px] ${theme.textMuted}`}>
            {stats.current} current · {stats.historical} hist.
          </span>
        </button>
      );
    })}
  </div>
);


const ForecastCardEditor = ({
  draft,
  originalDraft,
  onChange,
  lang,
  readOnly = false,
  stampClearAllowed = true,
}) => {
  const labels = getForecastFieldLabels(lang);
  const inputDisabled = readOnly ? { disabled: true } : {};
  const stampEditable = !readOnly || stampClearAllowed;

  const isChanged = (field) => !readOnly && originalDraft && draft[field] !== originalDraft[field];
  const fieldClass = (field) => `w-full px-3 py-2 text-sm ${theme.input} transition-colors ${isChanged(field) ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-400' : ''}`;

  return (
    <div className="mt-4 space-y-8 rounded-lg border border-olive-200 bg-olive-50 p-5">
      {/* Основная информация */}
      <section className="space-y-4">
        <h4 className={`text-sm font-bold ${theme.textPrimary} border-b border-olive-200 pb-1`}>Core Information</h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.direction}</span>
            <select value={draft.basis} onChange={(e) => onChange('basis', Number(e.target.value))} className={fieldClass('basis')} {...inputDisabled}>
              <option value={1}>↑ / Strengthening</option>
              <option value={0}>↓ / Weakening</option>
            </select>
          </label>
          <label className="space-y-1 md:col-span-2">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.subtitle}</span>
            <input type="text" value={draft.subbasis} onChange={(e) => onChange('subbasis', e.target.value)} className={fieldClass('subbasis')} {...inputDisabled} />
          </label>
          <label className="space-y-1 md:col-span-3">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.longTermTrend}</span>
            <input type="text" value={draft.long_term_trend} onChange={(e) => onChange('long_term_trend', e.target.value)} placeholder="down / вниз" className={fieldClass('long_term_trend')} {...inputDisabled} />
          </label>
        </div>
      </section>

      {/* Ценовые уровни и потенциал */}
      <section className="space-y-4">
        <h4 className={`text-sm font-bold ${theme.textPrimary} border-b border-olive-200 pb-1`}>Pricing Levels and Potential</h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.priceAsOfRelease}</span>
            <input type="number" step="0.01" value={draft.initial_price} onChange={(e) => onChange('initial_price', e.target.value)} className={fieldClass('initial_price')} {...inputDisabled} />
          </label>
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.nextTarget}</span>
            <input type="text" value={draft.next_targets} onChange={(e) => onChange('next_targets', e.target.value)} placeholder="120000, 130000" className={fieldClass('next_targets')} {...inputDisabled} />
          </label>
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.currentPrice}</span>
            <input type="number" step="0.01" value={draft.last_price} onChange={(e) => onChange('last_price', e.target.value)} className={fieldClass('last_price')} {...inputDisabled} />
          </label>
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.potential}</span>
            <input type="number" value={draft.target_roi} onChange={(e) => onChange('target_roi', e.target.value)} className={fieldClass('target_roi')} {...inputDisabled} />
          </label>
        </div>
      </section>

      {/* Даты и актуальность */}
      <section className="space-y-4">
        <h4 className={`text-sm font-bold ${theme.textPrimary} border-b border-olive-200 pb-1`}>Dates and Relevance</h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.priceAsOfReleaseMonth}</span>
            <input
              type="text"
              value={draft.forecast_date}
              onChange={(e) => onChange('forecast_date', e.target.value)}
              placeholder="May 2026 / Май 2026"
              className={fieldClass('forecast_date')}
              {...inputDisabled}
            />
          </label>
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.nextTargetMonth}</span>
            <input
              type="text"
              value={draft.target_date}
              onChange={(e) => onChange('target_date', e.target.value)}
              placeholder="June 2026 / Июнь 2026"
              className={fieldClass('target_date')}
              {...inputDisabled}
            />
          </label>
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.dataValidAsOf}</span>
            <input type="text" value={draft.end_date} onChange={(e) => onChange('end_date', e.target.value)} placeholder="14.05.2026 15:00" className={fieldClass('end_date')} {...inputDisabled} />
          </label>
        </div>
      </section>

      {/* Настройки печати */}
      <section className="space-y-4">
        <h4 className={`text-sm font-bold ${theme.textPrimary} border-b border-olive-200 pb-1`}>Archiving (Stamp)</h4>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.stamp}</span>
            <select
              value={draft.stamp}
              onChange={(e) => onChange('stamp', e.target.value)}
              className={fieldClass('stamp')}
              disabled={!stampEditable}
            >
              {!stampClearAllowed && draft.stamp ? null : <option value="">—</option>}
              <option value="done">Done</option>
              <option value="undone">Undone</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.stampDate}</span>
            <input
              type="text"
              value={draft.stamp_date}
              onChange={(e) => onChange('stamp_date', e.target.value)}
              placeholder="14 May 2026"
              disabled={!stampEditable || !draft.stamp}
              className={`w-full px-3 py-2 text-sm ${theme.input} disabled:bg-olive-100 disabled:text-olive-400 ${isChanged('stamp_date') ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-400' : ''}`}
            />
          </label>
        </div>
        {!stampClearAllowed && isArchivedStamp(draft.stamp) && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">
            The stamp can only be cleared if there is no other active forecast without a stamp. Please complete or delete the current one first.
          </p>
        )}
      </section>

      {/* Автоматизация и публикация */}
      {!readOnly && (
        <section className="space-y-4">
          <h4 className={`text-sm font-bold ${theme.textPrimary} border-b border-olive-200 pb-1`}>Automation and Publishing</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-1">
              <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.publishTime}</span>
              <input type="time" value={draft.publish_time} onChange={(e) => onChange('publish_time', e.target.value)} className={fieldClass('publish_time')} {...inputDisabled} />
              <span className="block text-[10px] text-olive-500 mt-1">This time is used for publishing via external signal</span>
            </label>
            <div className="flex items-center mt-4">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.process_now} onChange={(e) => onChange('process_now', e.target.checked)} disabled={readOnly} />
                <span className={`text-xs font-semibold ${theme.textSecondary}`}>{labels.publishOnSignal}</span>
              </label>
            </div>
            <label className="space-y-1 block md:col-span-2 mt-2">
              <span className={`block text-xs font-semibold ${theme.textSecondary}`}>{labels.channelCaption}</span>
              <textarea
                value={draft.channel_caption}
                onChange={(e) => onChange('channel_caption', e.target.value)}
                rows={2}
                className={`w-full px-3 py-2 text-sm ${theme.input} resize-none ${isChanged('channel_caption') ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-400' : ''}`}
                placeholder={labels.channelCaption}
              />
            </label>
          </div>
        </section>
      )}
    </div>
  );
};

const NewForecastForm = ({ formData, onChange, onSubmit, onCancel, loading }) => (
  <form onSubmit={onSubmit} className={`${theme.cardBg} ${theme.cardBorder} ${theme.cardShadow} rounded-xl p-5 space-y-5`}>
    <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>New Current Forecast</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <label className="space-y-2">
        <span className={`block text-sm font-medium ${theme.textSecondary}`}>Asset</span>
        <select name="title" value={formData.title} onChange={onChange} className={`w-full px-3 py-2 ${theme.input}`}>
          {FORECAST_TITLES.map((title) => (
            <option key={title} value={title}>{title}</option>
          ))}
        </select>
      </label>
      <label className="space-y-2">
        <span className={`block text-sm font-medium ${theme.textSecondary}`}>Language</span>
        <select name="lang" value={formData.lang} onChange={onChange} disabled={formData.title === 'RUB/USD'} className={`w-full px-3 py-2 ${theme.input}`}>
          {LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>{lang.toUpperCase()}</option>
          ))}
        </select>
      </label>
      <label className="space-y-2">
        <span className={`block text-sm font-medium ${theme.textSecondary}`}>Rate date (month)</span>
        <input
          type="text"
          name="forecast_date"
          value={formData.forecast_date}
          onChange={onChange}
          placeholder="May 2026"
          className={`w-full px-3 py-2 ${theme.input}`}
        />
      </label>
      <label className="space-y-2">
        <span className={`block text-sm font-medium ${theme.textSecondary}`}>Target date (month)</span>
        <input
          type="text"
          name="target_date"
          value={formData.target_date}
          onChange={onChange}
          placeholder="June 2026"
          className={`w-full px-3 py-2 ${theme.input}`}
        />
      </label>
      <label className="space-y-2">
        <span className={`block text-sm font-medium ${theme.textSecondary}`}>Initial price</span>
        <input type="number" step="0.01" name="initial_price" value={formData.initial_price} onChange={onChange} required className={`w-full px-3 py-2 ${theme.input}`} />
      </label>
      <label className="space-y-2 md:col-span-2">
        <span className={`block text-sm font-medium ${theme.textSecondary}`}>Targets</span>
        <input type="text" name="next_targets" value={formData.next_targets} onChange={onChange} required className={`w-full px-3 py-2 ${theme.input}`} />
      </label>
      <label className="space-y-2 md:col-span-3">
        <span className={`block text-sm font-medium ${theme.textSecondary}`}>Subtitle</span>
        <input type="text" name="subbasis" value={formData.subbasis} onChange={onChange} required className={`w-full px-3 py-2 ${theme.input}`} />
      </label>
    </div>
    <div className="flex gap-3">
      <button type="submit" disabled={loading} className={`px-4 py-2 rounded-lg font-semibold ${theme.btnPrimary}`}>Save</button>
      <button type="button" onClick={onCancel} className={`px-4 py-2 rounded-lg font-semibold ${theme.btnSecondary}`}>Cancel</button>
    </div>
  </form>
);

const ForecastCard = ({
  forecast,
  forecasts,
  draft,
  lang,
  isHistorical,
  imageCacheBust,
  busyForecastId,
  onChange,
  onSave,
  onGenerate,
  onPublish,
  onDelete,
}) => {
  const originalDraft = React.useMemo(() => forecastToDraft(forecast), [forecast]);
  const showImage = forecast.image_path || imageCacheBust;
  const stampClearAllowed = canClearForecastStamp(forecast, forecasts);
  const downloadName = buildForecastDownloadName(forecast);
  const [achievementStatus, setAchievementStatus] = useState('');

  const publishAsAchievement = async () => {
    setAchievementStatus('publishing');
    try {
      const imageResponse = await fetch(forecastAPI.getForecastImageUrl(forecast.id, imageCacheBust));
      if (!imageResponse.ok) throw new Error('Forecast image is not available');
      const blob = await imageResponse.blob();
      const query = new URLSearchParams({
        filename: `forecast-${forecast.id}-${Date.now()}.png`,
        name: [forecast.title, forecast.forecast_date].filter(Boolean).join(' '),
      });
      const response = await fetch(`${FROM_IMAGE_API_PATH}?${query}`, {
        method: 'POST',
        headers: { 'Content-Type': blob.type || 'image/png' },
        body: blob,
      });
      if (!response.ok) throw new Error('Achievement was not created');
      setAchievementStatus('published');
    } catch (err) {
      setAchievementStatus(`failed: ${err.message}`);
    }
  };

  return (
    <div className={`${theme.cardBg} ${theme.cardBorder} ${theme.cardShadow} rounded-xl p-5 ${isHistorical ? 'opacity-95' : ''}`}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`text-lg font-bold ${theme.textPrimary}`}>
              {isHistorical ? 'Historical forecast' : 'Current forecast'}
            </h3>
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${theme.badge}`}>#{forecast.id}</span>
            {forecast.process_now && !isHistorical && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-md bg-emerald-100 text-emerald-800">process_now</span>
            )}
            {forecast.stamp_status && (
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${
                forecast.stamp_status === 'done' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'
              }`}>
                {forecast.stamp_status}{forecast.stamp_date ? ` ${forecast.stamp_date}` : ''}
              </span>
            )}
          </div>
          <p className={`text-sm ${theme.textMuted} mt-1`}>{draft.forecast_date || forecast.forecast_date}</p>
          <p className={`text-sm ${theme.textSecondary} mt-2`}>{draft.subbasis || forecast.subbasis || forecast.description}</p>
        </div>
        <button type="button" onClick={() => onDelete(forecast)} className={`px-3 py-2 rounded-lg text-sm font-semibold ${theme.btnDanger}`}>
          Delete
        </button>
      </div>

      {showImage && (
        <div className="mt-4">
          <img
            src={forecastAPI.getForecastImageUrl(forecast.id, imageCacheBust)}
            alt={`${forecast.title} forecast`}
            className="max-h-96 w-full object-contain"
          />
        </div>
      )}
      {showImage && (
        <div className="mt-2 flex justify-end gap-2">
          {achievementStatus && (
            <span className={`self-center text-xs ${achievementStatus.startsWith('failed') ? 'text-red-600' : theme.textMuted}`}>
              {achievementStatus === 'published' ? 'Published as achievement' : achievementStatus}
            </span>
          )}
          <button
            type="button"
            onClick={publishAsAchievement}
            disabled={achievementStatus === 'publishing'}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${theme.btnSecondary} disabled:opacity-50`}
          >
            Publish as achievement
          </button>
          <a
            href={forecastAPI.getForecastImageUrl(forecast.id, imageCacheBust)}
            download={downloadName}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${theme.btnSecondary}`}
          >
            Download image
          </a>
        </div>
      )}

      <ForecastCardEditor
        draft={draft}
        originalDraft={originalDraft}
        lang={lang}
        readOnly={isHistorical}
        stampClearAllowed={stampClearAllowed}
        onChange={onChange}
      />

      {!isHistorical && (
        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => onSave(forecast)}
            disabled={busyForecastId === forecast.id}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${theme.btnSecondary} disabled:opacity-50`}
          >
            Save settings
          </button>
          <button
            type="button"
            onClick={() => onGenerate(forecast)}
            disabled={busyForecastId === forecast.id}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${theme.btnSecondary} disabled:opacity-50`}
          >
            {busyForecastId === forecast.id ? 'Working...' : 'Preview Image'}
          </button>
          <button
            type="button"
            onClick={() => onPublish(forecast)}
            disabled={busyForecastId === forecast.id}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${theme.btnPrimary} disabled:opacity-50`}
          >
            Check and publish
          </button>
        </div>
      )}
      {isHistorical && stampClearAllowed && (
        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => onSave(forecast)}
            disabled={busyForecastId === forecast.id}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${theme.btnSecondary} disabled:opacity-50`}
          >
            Clear stamp
          </button>
        </div>
      )}
    </div>
  );
};

const PublishModal = ({ state, onClose, onStep1Submit, onConfirm, setPublishTime, setProcessNow }) => {
  if (!state.isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-3xl rounded-2xl ${theme.cardBg} ${theme.cardShadow} p-6 max-h-[90vh] overflow-y-auto`}>
        <h2 className={`text-2xl font-bold ${theme.textPrimary} mb-6`}>
          {state.step === 1 ? 'Publish Settings' : 'Pre-publish check'}
        </h2>

        {state.step === 1 && (
          <div className="space-y-6">
            <div className="space-y-2 border border-olive-200 rounded-xl p-4 bg-olive-50">
              <label className={`block text-sm font-bold ${theme.textPrimary}`}>Publish Time</label>
              <input 
                type="time" 
                value={state.publishTime} 
                onChange={(e) => setPublishTime(e.target.value)}
                className={`w-full max-w-xs px-3 py-2 text-sm ${theme.input}`}
              />
              <p className={`text-xs ${theme.textSecondary}`}>This time is used for publishing via external signal.</p>
            </div>
            
            <div className="flex items-center border border-olive-200 rounded-xl p-4 bg-olive-50">
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={state.processNow}
                  onChange={(e) => setProcessNow(e.target.checked)}
                  className="w-5 h-5 accent-olive-600 shrink-0"
                />
                <div className="flex flex-col">
                  <span className={`text-sm font-bold ${theme.textPrimary}`}>Publish immediately</span>
                  <span className={`text-xs ${theme.textSecondary}`}>Publish images immediately, without waiting for an external signal</span>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button 
                type="button" 
                onClick={onClose} 
                className={`px-4 py-2 rounded-lg font-semibold ${theme.btnGhost}`}
                disabled={state.isGenerating}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={onStep1Submit} 
                className={`px-6 py-2 rounded-lg font-semibold ${theme.btnPrimary} flex items-center gap-2 disabled:opacity-50`}
                disabled={state.isGenerating}
              >
                {state.isGenerating ? 'Preparing...' : 'Next'}
              </button>
            </div>
          </div>
        )}

        {state.step === 2 && (
          <div className="space-y-6">
            <p className={`text-sm ${theme.textSecondary}`}>Please check the generated forecasts before confirming publication.</p>
            
            <div className="space-y-4">
              {state.activeForecasts.map(f => (
                <div key={f.id} className="border border-olive-200 rounded-xl p-4 bg-olive-50/50">
                  <h4 className="text-sm font-bold text-olive-800 mb-2">{f.title} ({f.lang?.toUpperCase() || 'RU'})</h4>
                  {state.images[f.id] ? (
                    <img src={state.images[f.id]} alt={`Preview ${f.lang}`} className="w-full max-h-96 object-contain rounded-lg border border-olive-100 bg-white" />
                  ) : (
                    <div className="h-32 flex items-center justify-center text-olive-400 text-sm">Preview not available</div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button 
                type="button" 
                onClick={onClose} 
                className={`px-4 py-2 rounded-lg font-semibold ${theme.btnGhost}`}
                disabled={state.isGenerating}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={onConfirm} 
                className={`px-6 py-2 rounded-lg font-semibold ${theme.btnPrimary} flex items-center gap-2 disabled:opacity-50`}
                disabled={state.isGenerating}
              >
                {state.isGenerating ? 'Publishing...' : 'Confirm Publish'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ForecastsPage = () => {
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [selectedLang, setSelectedLang] = useState(null);
  const [summaryForecasts, setSummaryForecasts] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newForm, setNewForm] = useState(defaultForecastForm);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageVersions, setImageVersions] = useState({});
  const [busyForecastId, setBusyForecastId] = useState(null);

  const [publishModalState, setPublishModalState] = useState({
    isOpen: false,
    step: 1,
    forecast: null,
    activeForecasts: [],
    publishTime: '',
    processNow: false,
    images: {},
    isGenerating: false,
  });

  const summary = summarizeForecastsBySymbol(summaryForecasts);

  const loadSummary = useCallback(async () => {
    try {
      const data = await forecastAPI.getForecasts(0, 500);
      setSummaryForecasts(data);
    } catch (err) {
      setError(`Failed to load forecasts summary: ${err.message}`);
    }
  }, []);

  const loadForecastsForSelection = useCallback(async (title, lang) => {
    setLoading(true);
    setError('');
    try {
      const filters = { title };
      if (title !== 'RUB/USD') filters.lang = lang;
      const data = await forecastAPI.getForecasts(0, 200, filters);
      setForecasts(data);
      setDrafts({});
    } catch (err) {
      setError(`Failed to load forecasts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!selectedTitle) return;
    if (selectedTitle !== 'RUB/USD' && !selectedLang) return;
    loadForecastsForSelection(selectedTitle, selectedLang);
  }, [selectedTitle, selectedLang, loadForecastsForSelection]);

  const handleSymbolSelect = (title) => {
    setSelectedTitle(title);
    setSelectedLang(title === 'RUB/USD' ? 'ru' : null);
    setShowAddForm(false);
    setError('');
  };

  const handleLanguageSelect = (lang) => {
    setSelectedLang(lang);
    setShowAddForm(false);
    setError('');
  };

  const handleBackToSymbols = () => {
    setSelectedTitle(null);
    setSelectedLang(null);
    setForecasts([]);
    setShowAddForm(false);
    loadSummary();
  };

  const handleBackToLanguages = () => {
    setSelectedLang(null);
    setForecasts([]);
    setShowAddForm(false);
  };

  const getDraft = (forecast) => drafts[forecast.id] ?? forecastToDraft(forecast);

  const updateDraft = (forecastId, field, value) => {
    setDrafts((current) => {
      const forecast = forecasts.find((item) => item.id === forecastId);
      return {
        ...current,
        [forecastId]: {
          ...(current[forecastId] ?? (forecast ? forecastToDraft(forecast) : {})),
          [field]: value,
        },
      };
    });
  };

  const refreshForecast = async (forecastId) => {
    const updated = await forecastAPI.getForecast(forecastId);
    if (!updated) return null;
    setForecasts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    setSummaryForecasts((current) => {
      const exists = current.some((item) => item.id === updated.id);
      if (exists) return current.map((item) => (item.id === updated.id ? updated : item));
      return [updated, ...current];
    });
    setDrafts((current) => {
      const next = { ...current };
      if (next[forecastId]) {
        const draft = next[forecastId];
        const newDraft = forecastToDraft(updated);
        next[forecastId] = {
          ...newDraft,
          end_date: draft.end_date,
          target_roi: draft.target_roi,
          channel_caption: draft.channel_caption,
        };
      }
      return next;
    });
    return updated;
  };

  const handleNewFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setNewForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const openAddForm = () => {
    if (showAddForm) {
      setShowAddForm(false);
      return;
    }
    setNewForm({
      ...defaultForecastForm,
      title: selectedTitle || defaultForecastForm.title,
      lang: selectedTitle === 'RUB/USD' ? 'ru' : (selectedLang || 'ru'),
    });
    setShowAddForm(true);
  };

  const handleCreateForecast = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await forecastAPI.createForecast(buildForecastPayload(newForm));
      await loadSummary();
      await loadForecastsForSelection(selectedTitle, selectedLang);
      setShowAddForm(false);
      setNewForm(defaultForecastForm);
    } catch (err) {
      setError(`Failed to create forecast: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForecast = async (forecast) => {
    const draft = getDraft(forecast);
    if (isArchivedStamp(forecast.stamp_status) && draft.stamp === '' && !canClearForecastStamp(forecast, forecasts)) {
      setError('Cannot clear stamp while another active forecast exists for this symbol and language.');
      return;
    }

    setBusyForecastId(forecast.id);
    setError('');
    try {
      await forecastAPI.updateForecast(forecast.id, buildForecastPayload(draft));
      await refreshForecast(forecast.id);
      await loadSummary();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(`Failed to save forecast: ${detail || err.message}`);
    } finally {
      setBusyForecastId(null);
    }
  };

  const runImageAction = async (forecast, { sendToChannel }) => {
    const draft = getDraft(forecast);
    const payload = buildGenerationPayload(forecast, draft, { sendToChannel });

    if (!payload.forecast_rate || !payload.next_target) {
      setError('Price as of Release and Next Target are required.');
      return;
    }

    setBusyForecastId(forecast.id);
    setError('');
    try {
      await forecastAPI.updateForecast(forecast.id, buildForecastPayload(draft));
      await forecastAPI.generateForecastImage(payload);
      await refreshForecast(forecast.id);
      setImageVersions((current) => ({ ...current, [forecast.id]: Date.now() }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((item) => item.msg || item).join(', ')
        : detail || err.message;
      setError(
        sendToChannel
          ? `Failed to publish forecast to channel: ${message}`
          : `Failed to generate forecast image: ${message}`,
      );
    } finally {
      setBusyForecastId(null);
    }
  };

  const handleGenerate = (forecast) => runImageAction(forecast, { sendToChannel: false });
  
  const handleCheckAndPublish = async (forecast) => {
    setLoading(true);
    try {
      const ruForecasts = await forecastAPI.getForecasts(0, 50, { title: forecast.title, lang: 'ru' });
      const enForecasts = forecast.title === 'RUB/USD' ? [] : await forecastAPI.getForecasts(0, 50, { title: forecast.title, lang: 'en' });
      
      const allSymbolForecasts = [...ruForecasts, ...enForecasts];
      const activeSymbolForecasts = allSymbolForecasts.filter(f => !isArchivedStamp(f.stamp_status));
      
      const draft = getDraft(forecast);
      
      setPublishModalState({
        isOpen: true,
        step: 1,
        forecast: forecast,
        activeForecasts: activeSymbolForecasts,
        publishTime: draft.publish_time || '01:00',
        processNow: draft.process_now || false,
        images: {},
        isGenerating: false,
      });
    } catch (err) {
      setError(`Failed to prepare publication: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishModalStep1Submit = async () => {
    setPublishModalState(prev => ({ ...prev, isGenerating: true }));
    try {
      const newImages = {};
      const syncedDrafts = {};
      const sourceDraft = drafts[publishModalState.forecast.id] || forecastToDraft(publishModalState.forecast);
      
      for (const f of publishModalState.activeForecasts) {
        let currentDraft = drafts[f.id] || forecastToDraft(f);
        
        // Sync fields from the edited forecast to the other language forecast
        if (f.id !== publishModalState.forecast.id) {
          const targetLang = getForecastLangKey(f);
          const sourceLang = getForecastLangKey(publishModalState.forecast);
          
          let syncedEndDate = sourceDraft.end_date;
          if (sourceLang === 'ru' && targetLang === 'en') {
            syncedEndDate = shiftDateString(syncedEndDate, -3);
          } else if (sourceLang === 'en' && targetLang === 'ru') {
            syncedEndDate = shiftDateString(syncedEndDate, 3);
          }

          currentDraft = {
            ...currentDraft,
            forecast_date: translateMonthString(sourceDraft.forecast_date, targetLang),
            initial_price: sourceDraft.initial_price,
            next_targets: sourceDraft.next_targets,
            last_price: sourceDraft.last_price,
            target_roi: sourceDraft.target_roi,
            basis: sourceDraft.basis,
            stamp: sourceDraft.stamp,
            stamp_date: sourceDraft.stamp_date,
            target_date: translateMonthString(sourceDraft.target_date, targetLang),
            end_date: syncedEndDate,
          };
        }
        
        const updatedDraft = {
          ...currentDraft,
          publish_time: publishModalState.publishTime,
          process_now: publishModalState.processNow,
        };
        
        const payload = buildGenerationPayload(f, updatedDraft, { sendToChannel: false });
        if (!payload.forecast_rate || !payload.next_target) {
          throw new Error(`Forecast ${f.lang || 'ru'} is missing Rate or Target`);
        }
        await forecastAPI.generateForecastImage(payload);
        
        const cacheBust = Date.now();
        setImageVersions(current => ({ ...current, [f.id]: cacheBust }));
        newImages[f.id] = forecastAPI.getForecastImageUrl(f.id, cacheBust);
        syncedDrafts[f.id] = updatedDraft;
      }
      
      setPublishModalState(prev => ({
        ...prev,
        step: 2,
        images: newImages,
        syncedDrafts,
        isGenerating: false,
      }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(`Error generating previews: ${detail || err.message}`);
      setPublishModalState(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handlePublishModalConfirm = async () => {
    setPublishModalState(prev => ({ ...prev, isGenerating: true }));
    try {
        // First, save all synced drafts to the database
        for (const f of publishModalState.activeForecasts) {
            const currentDraft = publishModalState.syncedDrafts[f.id] || drafts[f.id] || forecastToDraft(f);
            await forecastAPI.updateForecast(f.id, buildForecastPayload(currentDraft));
        }

        if (publishModalState.processNow) {
            for (const f of publishModalState.activeForecasts) {
                const currentDraft = publishModalState.syncedDrafts[f.id] || drafts[f.id] || forecastToDraft(f);
                const payload = buildGenerationPayload(f, currentDraft, { sendToChannel: true });
                await forecastAPI.generateForecastImage(payload);
            }
            alert("Forecasts successfully published!");
        } else {
            await forecastAPI.scheduleManualForecast({
                pair: publishModalState.forecast.title,
                publish_time: publishModalState.publishTime,
                process_now: false
            });
            alert("Forecasts successfully scheduled for " + publishModalState.publishTime + "!");
        }
        
        setPublishModalState(prev => ({ ...prev, isOpen: false }));
        await loadSummary();
        if (selectedTitle || selectedLang) {
            await loadForecastsForSelection(selectedTitle, selectedLang);
        }
    } catch (err) {
        const detail = err.response?.data?.detail;
        alert(`Error publishing: ${detail || err.message}`);
        setPublishModalState(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleDelete = async (forecast) => {
    const label = `${forecast.title}${forecast.lang ? ` (${forecast.lang})` : ''}`;
    if (!window.confirm(`Delete forecast ${label} #${forecast.id}?`)) return;

    setLoading(true);
    setError('');
    try {
      await forecastAPI.deleteForecast(forecast.id);
      setDrafts((current) => {
        const next = { ...current };
        delete next[forecast.id];
        return next;
      });
      await loadSummary();
      await loadForecastsForSelection(selectedTitle, selectedLang);
    } catch (err) {
      setError(`Failed to delete forecast: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const { current, historical } = splitForecastsByArchiveStatus(forecasts);
  const breadcrumbLang = selectedTitle === 'RUB/USD' ? null : selectedLang?.toUpperCase();

  return (
    <div className={`space-y-8 px-4 py-8 ${theme.pageBg}`}>
      <PublishModal 
        state={publishModalState}
        onClose={() => setPublishModalState(prev => ({ ...prev, isOpen: false }))}
        onStep1Submit={handlePublishModalStep1Submit}
        onConfirm={handlePublishModalConfirm}
        setPublishTime={(val) => setPublishModalState(prev => ({ ...prev, publishTime: val }))}
        setProcessNow={(val) => setPublishModalState(prev => ({ ...prev, processNow: val }))}
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${theme.textPrimary}`}>Forecasts</h1>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>
            Select symbol and language, then manage current and historical forecasts.
          </p>
        </div>
        {(selectedTitle && (selectedLang || selectedTitle === 'RUB/USD')) && (
          <div className="flex gap-2">
            <button type="button" onClick={openAddForm} className={`px-4 py-2 rounded-lg font-semibold ${theme.btnPrimary}`} disabled={loading}>
              {showAddForm ? 'Cancel add' : 'Add forecast'}
            </button>
            <button
              type="button"
              onClick={() => loadForecastsForSelection(selectedTitle, selectedLang)}
              className={`px-4 py-2 rounded-lg font-semibold ${theme.btnSecondary}`}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        )}
      </div>

      {(selectedTitle || selectedLang) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {selectedTitle && (
            <button type="button" onClick={handleBackToSymbols} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg ${theme.btnGhost}`}>
              <ChevronLeft className="w-4 h-4" />
              Symbols
            </button>
          )}
          {selectedTitle && selectedTitle !== 'RUB/USD' && selectedLang && (
            <button type="button" onClick={handleBackToLanguages} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg ${theme.btnGhost}`}>
              <ChevronLeft className="w-4 h-4" />
              {selectedTitle}
            </button>
          )}
          {selectedTitle && (
            <span className={`px-3 py-1.5 rounded-lg bg-olive-50 ${theme.textSecondary} font-semibold`}>
              {selectedTitle}{breadcrumbLang ? ` · ${breadcrumbLang}` : ''}
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!selectedTitle && (
        <div className="space-y-3">
          <h3 className={`text-sm font-semibold ${theme.textSecondary}`}>Select symbol</h3>
          <SymbolPicker summary={summary} onSelect={handleSymbolSelect} />
        </div>
      )}

      {selectedTitle && selectedTitle !== 'RUB/USD' && !selectedLang && (
        <div className="space-y-3">
          <h3 className={`text-sm font-semibold ${theme.textSecondary}`}>Select language for {selectedTitle}</h3>
          <LanguagePicker title={selectedTitle} summary={summary} onSelect={handleLanguageSelect} />
        </div>
      )}

      {selectedTitle && (selectedLang || selectedTitle === 'RUB/USD') && (
        <div className="space-y-6">
          {showAddForm && (
            <NewForecastForm
              formData={newForm}
              onChange={handleNewFormChange}
              onSubmit={handleCreateForecast}
              onCancel={() => setShowAddForm(false)}
              loading={loading}
            />
          )}

          {loading && forecasts.length === 0 ? (
            <div className={`${theme.cardBg} ${theme.cardBorder} rounded-xl p-6 ${theme.textMuted}`}>Loading...</div>
          ) : (
            <>
              <section className="space-y-3">
                <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Current forecasts</h3>
                {current.length === 0 ? (
                  <div className={`${theme.cardBg} ${theme.cardBorder} rounded-xl p-6 ${theme.textMuted}`}>
                    No current forecasts for this symbol and language.
                  </div>
                ) : (
                  current.map((forecast) => {
                    const draft = getDraft(forecast);
                    const lang = draft.lang || forecast.lang || 'ru';
                    return (
                      <ForecastCard
                        key={forecast.id}
                        forecast={forecast}
                        forecasts={forecasts}
                        draft={draft}
                        lang={lang}
                        isHistorical={false}
                        imageCacheBust={imageVersions[forecast.id]}
                        busyForecastId={busyForecastId}
                        onChange={(field, value) => updateDraft(forecast.id, field, value)}
                        onSave={handleSaveForecast}
                        onGenerate={handleGenerate}
                        onPublish={handleCheckAndPublish}
                        onDelete={handleDelete}
                      />
                    );
                  })
                )}
              </section>

              <section className="space-y-3">
                <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Historical forecasts</h3>
                {historical.length === 0 ? (
                  <div className={`${theme.cardBg} ${theme.cardBorder} rounded-xl p-6 ${theme.textMuted}`}>
                    No stamped historical forecasts yet.
                  </div>
                ) : (
                  historical.map((forecast) => {
                    const draft = getDraft(forecast);
                    const lang = draft.lang || forecast.lang || 'ru';
                    return (
                      <ForecastCard
                        key={forecast.id}
                        forecast={forecast}
                        forecasts={forecasts}
                        draft={draft}
                        lang={lang}
                        isHistorical
                        imageCacheBust={imageVersions[forecast.id]}
                        busyForecastId={busyForecastId}
                        onChange={(field, value) => updateDraft(forecast.id, field, value)}
                        onSave={handleSaveForecast}
                        onGenerate={handleGenerate}
                        onPublish={handleCheckAndPublish}
                        onDelete={handleDelete}
                      />
                    );
                  })
                )}
              </section>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ForecastsPage;
